/**
 * Player-facing settings, persisted per browser. Every read/write is guarded:
 * storage can be unavailable (private mode, blocked site data) and stored
 * values are untrusted, so each field is validated and clamped on load.
 *
 * Version 2 (Phase 5) adds audio, comfort, graphics, interface and key
 * binding options. A version 1 record is migrated: its four fields are kept.
 */
import { DEFAULT_BINDINGS, type Action, type Bindings } from '../input/InputManager';

export type GraphicsQuality = 'low' | 'medium' | 'high';
export type FlashLevel = 'full' | 'reduced' | 'off';
export type HudLayout = 'minimal' | 'expanded';

/** Actions a player can rebind (developer keys stay fixed). */
export const REBINDABLE: readonly Action[] = ['forward', 'back', 'left', 'right', 'jump', 'crouch', 'restart', 'restartToStart', 'toggleMode'];
export type RebindableAction = (typeof REBINDABLE)[number];

export const ACTION_LABELS: Readonly<Record<Action, string>> = Object.freeze({
  forward: 'Move forward',
  back: 'Move back',
  left: 'Strafe left',
  right: 'Strafe right',
  jump: 'Jump',
  crouch: 'Crouch / slide',
  restart: 'Restart (checkpoint in practice)',
  restartToStart: 'Restart from the start',
  toggleMode: 'Full run / practice',
  toggleDebug: 'Diagnostics',
  devBoost: 'Developer boost',
  toggleDevPanel: 'Developer tools',
  placeSpawn: 'Place lab spawn',
  gotoSpawn: 'Go to lab spawn',
});

/** Keys that are never bindable: Esc pauses, F2/F3 are developer keys, the rest belong to the browser or OS. */
export const RESERVED_KEYS: ReadonlySet<string> = new Set(['Escape', 'F2', 'F3', 'F5', 'F11', 'F12', 'Tab', 'MetaLeft', 'MetaRight', 'ContextMenu']);
/** Most keys one action may have. */
export const MAX_KEYS_PER_ACTION = 2;
const CODE_PATTERN = /^[A-Za-z][A-Za-z0-9]{0,23}$/;

export interface Settings {
  sensitivity: number;
  fovDeg: number;
  renderScale: number;
  /** Subtle camera roll from lateral speed. */
  roll: boolean;
  /** 0..1 */
  masterVolume: number;
  musicVolume: number;
  effectsVolume: number;
  /** Movement-reactive sound (rushing air). */
  speedSound: boolean;
  /** Death, checkpoint and finish screen flashes. */
  flashes: FlashLevel;
  /** No interface animation and no camera roll. */
  reducedMotion: boolean;
  graphics: GraphicsQuality;
  /** Interface text scale. */
  uiScale: number;
  /** Full-strength outlines and brighter interface text. */
  highContrast: boolean;
  hud: HudLayout;
  /** Overrides of the default keys for rebindable actions. */
  bindings: Partial<Record<RebindableAction, readonly string[]>>;
  /** Which ghost races with you: your personal best, your previous attempt, or none. */
  ghost: GhostSource;
  /** 0.1..1 */
  ghostOpacity: number;
  /** Seconds the ghost runs ahead (positive) or starts later (negative). */
  ghostOffset: number;
  /** Colour (0xRRGGBB) of safe outlines in play and of the interface's lines. Never close to the hazard colours. */
  outlineColor: number;
}

export type GhostSource = 'off' | 'pb' | 'last';

export const DEFAULT_SETTINGS: Readonly<Settings> = Object.freeze({
  sensitivity: 1,
  fovDeg: 90,
  renderScale: 1,
  roll: false,
  masterVolume: 0.8,
  musicVolume: 0.6,
  effectsVolume: 0.8,
  speedSound: true,
  flashes: 'full' as FlashLevel,
  reducedMotion: false,
  graphics: 'medium' as GraphicsQuality,
  uiScale: 1,
  highContrast: false,
  hud: 'minimal' as HudLayout,
  bindings: Object.freeze({}),
  ghost: 'pb' as GhostSource,
  ghostOpacity: 0.35,
  ghostOffset: 0,
  outlineColor: 0xffffff,
});

/** Colours an outline may not resemble: lethal red and reactive cyan must stay unmistakable (SceneView). */
export const HAZARD_OUTLINES: readonly { name: string; color: number }[] = [
  { name: 'lethal red', color: 0xff3848 },
  { name: 'reactive cyan', color: 0x6ff2ff },
];

export const OUTLINE_PRESETS: readonly { name: string; color: number }[] = [
  { name: 'White', color: 0xffffff },
  { name: 'Bone', color: 0xe8dcc0 },
  { name: 'Amber', color: 0xffb02e },
  { name: 'Phosphor', color: 0x7dff6a },
  { name: 'Violet', color: 0xb48cff },
  { name: 'Gold', color: 0xffe14d },
];

/** Smallest allowed distance from a hazard colour (redmean RGB distance, 0..~765). */
const MIN_HAZARD_DISTANCE = 200;
/** Dimmest allowed outline (relative luminance), so lines stay visible on black. */
const MIN_OUTLINE_LUMINANCE = 0.15;

function rgb(c: number): [number, number, number] {
  return [(c >> 16) & 255, (c >> 8) & 255, c & 255];
}

/** Perceptually weighted RGB distance ("redmean"). */
function colourDistance(a: number, b: number): number {
  const [r1, g1, b1] = rgb(a);
  const [r2, g2, b2] = rgb(b);
  const rm = (r1 + r2) / 2;
  return Math.sqrt((2 + rm / 256) * (r1 - r2) ** 2 + 4 * (g1 - g2) ** 2 + (2 + (255 - rm) / 256) * (b1 - b2) ** 2);
}

function luminance(c: number): number {
  const lin = (v: number): number => {
    const x = v / 255;
    return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = rgb(c);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Why `c` can't be the outline colour, or null if it can. */
export function outlineColourProblem(c: number): string | null {
  if (!Number.isInteger(c) || c < 0 || c > 0xffffff) return 'That is not a colour.';
  const near = HAZARD_OUTLINES.find((x) => colourDistance(c, x.color) < MIN_HAZARD_DISTANCE);
  if (near) return `Too close to ${near.name}: safe edges must never look like hazards.`;
  return luminance(c) < MIN_OUTLINE_LUMINANCE ? 'Too dark: outlines would disappear on black.' : null;
}

/** "#rrggbb" for a 0xRRGGBB colour. */
export function colourHex(c: number): string {
  return `#${c.toString(16).padStart(6, '0')}`;
}

const KEY = 'xernous.settings.v2';
const KEY_V1 = 'xernous.settings.v1';
const ROLL_DEG = 1.5;

export const RANGES = {
  sensitivity: [0.2, 5],
  fovDeg: [60, 120],
  renderScale: [0.4, 1],
  volume: [0, 1],
  uiScale: [0.8, 1.5],
  ghostOpacity: [0.1, 1],
  ghostOffset: [-10, 10],
} as const;

function num(v: unknown, [min, max]: readonly [number, number], fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : fallback;
}

function oneOf<T extends string>(v: unknown, options: readonly T[], fallback: T): T {
  return typeof v === 'string' && (options as readonly string[]).includes(v) ? (v as T) : fallback;
}

const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback);

export function isBindableCode(code: unknown): code is string {
  return typeof code === 'string' && CODE_PATTERN.test(code) && !RESERVED_KEYS.has(code);
}

/** The full binding table: defaults with the player's overrides applied. */
export function effectiveBindings(overrides: Settings['bindings']): Bindings {
  const out = { ...DEFAULT_BINDINGS } as Record<Action, readonly string[]>;
  for (const a of REBINDABLE) {
    const o = overrides[a as RebindableAction];
    if (o) out[a] = [...o];
  }
  return out;
}

/** The binding table for the game (developer actions have no keys there). */
export function gameBindings(overrides: Settings['bindings']): Bindings {
  return { ...effectiveBindings(overrides), devBoost: [], toggleDevPanel: [], placeSpawn: [], gotoSpawn: [] };
}

/** Keys bound to more than one action, with the actions sharing each. */
export function bindingConflicts(bindings: Readonly<Bindings>): { code: string; actions: Action[] }[] {
  const byCode = new Map<string, Action[]>();
  for (const [action, codes] of Object.entries(bindings) as [Action, readonly string[]][]) {
    for (const code of codes) byCode.set(code, [...(byCode.get(code) ?? []), action]);
  }
  return [...byCode].filter(([, actions]) => actions.length > 1).map(([code, actions]) => ({ code, actions }));
}

function sanitizeBindings(raw: unknown): Settings['bindings'] {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};
  const out: Partial<Record<RebindableAction, readonly string[]>> = {};
  for (const a of REBINDABLE) {
    const v = (raw as Record<string, unknown>)[a];
    if (!Array.isArray(v)) continue;
    const codes = [...new Set(v.filter(isBindableCode))].slice(0, MAX_KEYS_PER_ACTION);
    if (codes.length) out[a as RebindableAction] = codes;
  }
  // A stored table that conflicts (edited by hand, or from another version) is dropped, not half-applied.
  return bindingConflicts(gameBindings(out)).length ? {} : out;
}

export function sanitizeSettings(raw: unknown): Settings {
  const r = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  const d = DEFAULT_SETTINGS;
  return {
    sensitivity: num(r['sensitivity'], RANGES.sensitivity, d.sensitivity),
    fovDeg: num(r['fovDeg'], RANGES.fovDeg, d.fovDeg),
    renderScale: num(r['renderScale'], RANGES.renderScale, d.renderScale),
    roll: bool(r['roll'], d.roll),
    masterVolume: num(r['masterVolume'], RANGES.volume, d.masterVolume),
    musicVolume: num(r['musicVolume'], RANGES.volume, d.musicVolume),
    effectsVolume: num(r['effectsVolume'], RANGES.volume, d.effectsVolume),
    speedSound: bool(r['speedSound'], d.speedSound),
    flashes: oneOf(r['flashes'], ['full', 'reduced', 'off'], d.flashes),
    reducedMotion: bool(r['reducedMotion'], d.reducedMotion),
    graphics: oneOf(r['graphics'], ['low', 'medium', 'high'], d.graphics),
    uiScale: num(r['uiScale'], RANGES.uiScale, d.uiScale),
    highContrast: bool(r['highContrast'], d.highContrast),
    hud: oneOf(r['hud'], ['minimal', 'expanded'], d.hud),
    bindings: sanitizeBindings(r['bindings']),
    ghost: oneOf(r['ghost'], ['off', 'pb', 'last'], d.ghost),
    ghostOpacity: num(r['ghostOpacity'], RANGES.ghostOpacity, d.ghostOpacity),
    ghostOffset: num(r['ghostOffset'], RANGES.ghostOffset, d.ghostOffset),
    outlineColor: typeof r['outlineColor'] === 'number' && outlineColourProblem(r['outlineColor']) === null ? r['outlineColor'] : d.outlineColor,
  };
}

function prefersReducedMotion(): boolean {
  try {
    return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

/**
 * Whether WebGL is drawn on the CPU (SwiftShader, llvmpipe and similar).
 * There every preset step costs a lot: on SwiftShader, Lantern Spire ran at
 * 52 fps on low against 27 on medium (docs/PERFORMANCE.md).
 */
export function softwareRendering(): boolean {
  try {
    const gl = document.createElement('canvas').getContext('webgl2');
    if (!gl) return false;
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    const name = String(info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return /swiftshader|llvmpipe|softpipe|software|basic render/i.test(name);
  } catch {
    return false;
  }
}

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return sanitizeSettings(JSON.parse(raw));
    const v1 = localStorage.getItem(KEY_V1);
    // First visit: follow the system's reduced-motion preference, and start software rendering on the low preset.
    return sanitizeSettings({
      ...(v1 ? (JSON.parse(v1) as object) : {}),
      ...(prefersReducedMotion() ? { reducedMotion: true, flashes: 'reduced' } : {}),
      ...(softwareRendering() ? { graphics: 'low' } : {}),
    });
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(s: Readonly<Settings>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch (err: unknown) {
    console.warn('Settings could not be saved; they will apply for this session only.', err);
  }
}

export function rollDegrees(s: Readonly<Settings>): number {
  return s.roll && !s.reducedMotion ? ROLL_DEG : 0;
}

/** A readable key name for a KeyboardEvent.code ("KeyW" → "W"). */
export function keyLabel(code: string): string {
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit\d$/.test(code)) return code.slice(5);
  const named: Record<string, string> = { Space: 'Space', ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→', ShiftLeft: 'Left Shift', ShiftRight: 'Right Shift', ControlLeft: 'Left Ctrl', ControlRight: 'Right Ctrl', AltLeft: 'Left Alt', AltRight: 'Right Alt', Enter: 'Enter', Backspace: 'Backspace' };
  return named[code] ?? code.replace(/^Numpad/, 'Num ');
}
